import React from "react";
import { Navigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { Role } from "@/shared/constants/roles";
import { FullPageLoadingSpinner } from "@/components/ui/loading-spinner";

interface RoleGuardProps {
  children: React.ReactNode;
  roles: Role[]; // Array of allowed roles
  requireAll?: boolean; // If true, user must have ALL roles; if false, user needs ANY role (default: false)
  fallbackPath?: string; // Where to redirect if access denied (default: "/")
}

/**
 * Role-based access guard
 * Checks if user has required role(s) before granting access
 * Shows loading spinner while validating
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  roles, 
  requireAll = false,
  fallbackPath = "/"
}) => {
  const { isAuthenticated, isLoading, hasAnyRole, hasAllRoles } = useUser();

  if (isLoading) {
    return <FullPageLoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const hasAccess = requireAll ? hasAllRoles(roles) : hasAnyRole(roles);

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

